import { ReactNode } from "react";
import { DateRange } from "../types";
import { DateRangePicker } from "./date-range-picker";

interface ReportHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  dateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
  actions?: ReactNode;
  children?: ReactNode;
}

export const ReportHeader = ({
  title,
  subtitle,
  icon,
  dateRange,
  onDateRangeChange,
  actions,
  children,
}: ReportHeaderProps) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Title Section */}
        <div className="flex items-center gap-4">
          {icon && (
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <div className="text-white">{icon}</div>
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>

        {/* Actions Section */}
        <div className="flex items-center gap-3 flex-wrap">
          {dateRange && onDateRangeChange && (
            <DateRangePicker value={dateRange} onChange={onDateRangeChange} />
          )}
          {actions}
        </div>
      </div>

      {/* Additional Filters/Content */}
      {children && <div className="mt-4 pt-4 border-t border-gray-100">{children}</div>}
    </div>
  );
};

// Export Button Component
export const ExportButton = ({ onClick, loading = false }: { onClick: () => void; loading?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50"
  >
    {loading ? (
      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    ) : (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    )}
    <span className="text-sm font-medium">Xuất Excel</span>
  </button>
);

// Refresh Button Component
export const RefreshButton = ({ onClick, loading = false }: { onClick: () => void; loading?: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    className="flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
  >
    <svg
      className={`w-5 h-5 text-gray-600 ${loading ? "animate-spin" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  </button>
);

export default ReportHeader;
